module Client.Page.User.RimPro

open Client
open Client.Infrastructure.Api
open Client.Components
open Client.Domain
open Client.InfrastructureTypes
open Client.DomainTypes.Msg
open Elmish
open Fable.FontAwesome
open Fable.React.Props
open Feliz
open Fulma
open Fable.React
open Shared
open Shared.Dto
open Shared.Dto.Dto
open Shared.Dto.RimPro
open Shared.Infrastructure
open Thoth.Elmish


type DataModel = {
    Session: UserSession
    AvailableSensors: RimPro.SensorsResponseDto
    Stations: RimPro.StationDto list
    DeleteModal: RimPro.StationDto option
    CreationModal: Forms.RimPro.Model option
    Iframes: RimProIframeDto list
}

type LoadingModel = {
    Session: UserSession
    AvailableSensors: RimPro.SensorsResponseDto option
    Stations: RimPro.StationDto list option
    IFrames: RimProIframeDto list option
}

type Model = Loadable<DataModel, LoadingModel>

let init (session: UserSession) =
    let requestData = {
        SessionKey = session.SessionKey
        Data = ()
    }

    let cmds = [
        Cmd.OfAsync.perform api.getRimProAvailableSensors requestData (RimProMsg.SensorsReceived >> Msg.RimPro)
        Cmd.OfAsync.perform api.getMyRimProStations requestData (RimProMsg.StationsReceived >> Msg.RimPro)
        Cmd.OfAsync.perform api.getRimProIframes requestData (RimProMsg.IframesReceived >> Msg.RimPro)
    ]

    Loadable.Loading {
        LoadingModel.Session = session
        AvailableSensors = None
        Stations = None
        IFrames = None
    },
    Cmd.batch cmds

let private tryCreateDataModel (loadingModel: LoadingModel) : Loadable<DataModel, _> option =
    let factory =
        fun sensors stations iframes ->
            Loadable.Data {
                DataModel.Session = loadingModel.Session
                AvailableSensors = sensors
                Stations = stations
                DeleteModal = None
                CreationModal = None
                Iframes = iframes
            }

    factory
    |> Some
    |> Option.apply loadingModel.AvailableSensors
    |> Option.apply loadingModel.Stations
    |> Option.apply loadingModel.IFrames

let private updateForm (msg: RimProFormMsg) (dataModel: DataModel) =
    match dataModel.CreationModal with
    | Some model ->
        let (newModel, result) = Forms.RimPro.update msg model

        match result with
        | Forms.RimPro.Noop -> { dataModel with CreationModal = Some newModel }, Cmd.none
        | Forms.RimPro.FormResult.CloseModal -> { dataModel with CreationModal = None }, Cmd.none
        | Forms.RimPro.FormResult.Create newStation ->
            let requestData = {
                SessionKey = dataModel.Session.SessionKey
                Data = newStation
            }

            { dataModel with CreationModal = Some newModel },
            Cmd.OfAsync.perform api.createRimProStation requestData RimProMsg.StationCreated
    | None -> dataModel, Cmd.none

let private updateDataModel (msg: RimProMsg) (dataModel: DataModel) =
    match msg with
    | RimProMsg.OpenDeleteModal station -> Loadable.Data { dataModel with DeleteModal = Some station }, Cmd.none
    | RimProMsg.CloseDeleteModal -> Loadable.Data { dataModel with DeleteModal = None }, Cmd.none
    | RimProMsg.DeleteStation station ->
        let requestData = {
            SessionKey = dataModel.Session.SessionKey
            Data = station.Id
        }

        Loadable.Data { dataModel with DeleteModal = None },
        Cmd.OfAsync.perform api.deleteRimProStation requestData (RimProMsg.StationDeleted >> RimPro)
    | OpenCreationModal ->
        Loadable.Data {
            dataModel with
                CreationModal =
                    Forms.RimPro.init
                        dataModel.AvailableSensors.AirSensors
                        dataModel.AvailableSensors.RainSensors
                        dataModel.AvailableSensors.LeafletWetnessSensors
                    |> Some
        },
        Cmd.none
    | RimProMsg.FormMsg formMsg -> updateForm formMsg dataModel |> Tuple.map Loadable.Data (Cmd.map RimPro)
    | RimProMsg.StationCreated result ->
        match result with
        | Ok true ->
            let toastCmd =
                Toast.create "RimPro Station wurde erfolgreich erstellt" |> Toast.success

            init dataModel.Session
            |> Tuple.mapSecond (fun cmd -> Cmd.batch [ toastCmd; cmd ])
        | Ok false ->
            let cmd =
                ErrorConfirmation.confirmation ErrorConfirmation.defaultConfig "Fehler beim Erstellen der Station" true

            updateForm RimProFormMsg.CreationFinished dataModel
            |> Tuple.map Loadable.Data (Cmd.map RimPro >> fun fc -> Cmd.batch [ fc; cmd ])
        | AuthenticatedResponse.Error err ->
            let cmd = ErrorConfirmation.responseError ErrorConfirmation.defaultConfig err

            updateForm RimProFormMsg.CreationFinished dataModel
            |> Tuple.map Loadable.Data (Cmd.map RimPro >> fun fc -> Cmd.batch [ fc; cmd ])
    | _ -> Loadable.Data dataModel, Cmd.none

let private updateLoadingModel (msg: RimProMsg) (model: LoadingModel) =
    match msg with
    | SensorsReceived response ->
        match response with
        | Ok airSensors ->
            let newLoadingModel = { model with AvailableSensors = Some airSensors }

            let newModel =
                newLoadingModel
                |> tryCreateDataModel
                |> Option.defaultValue (Loadable.Loading newLoadingModel)

            newModel, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | RimProMsg.StationsReceived response ->
        match response with
        | Ok stations ->
            let newLoadingModel = { model with Stations = Some stations }

            let newModel =
                newLoadingModel
                |> tryCreateDataModel
                |> Option.defaultValue (Loadable.Loading newLoadingModel)

            newModel, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | RimProMsg.IframesReceived response ->
        match response with
        | Ok iframes ->
            let newLoadingModel = { model with IFrames = Some iframes }

            let newModel =
                newLoadingModel
                |> tryCreateDataModel
                |> Option.defaultValue (Loadable.Loading newLoadingModel)

            newModel, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | _ -> Loadable.Loading model, Cmd.none

let update (msg: RimProMsg) (model: Model) =
    match (msg, model) with
    | RimProMsg.StationDeleted response, Loadable.Data dataModel ->
        match response with
        | Ok result ->
            if result then
                init dataModel.Session
            else
                let toastCmd = Toast.create "Fehler beim Löschen der Station" |> Toast.error

                Loadable.Data dataModel, toastCmd
        | AuthenticatedResponse.Error err ->
            let cmd = ErrorConfirmation.responseError ErrorConfirmation.defaultConfig err

            Loadable.Data dataModel, cmd
    | _, Loadable.Data dataModel ->
        let newDataModel, cmd = updateDataModel msg dataModel

        newDataModel, cmd
    | _, Loadable.Loading loadingModel ->
        let newModel, cmd = updateLoadingModel msg loadingModel

        newModel, cmd
    | _, _ -> model, Cmd.none

let private addRowButton dispatch =
    let button =
        Button.button [
            Button.Color IsLink
            CommonButtons.onClick (fun _ -> dispatch OpenCreationModal)
        ] [
            Icon.icon [] [ Fa.i [ Fa.Solid.Plus ] [] ]
            span [] [ str "Hinzufügen" ]
        ]

    Level.level [] [ Level.left [] []; Level.right [] [ button ] ]

let private deleteButton dispatch (station: RimPro.StationDto) : ReactElement =
    Button.button [
        Button.Color Color.IsDanger
        CommonButtons.onClick (fun _ -> RimProMsg.OpenDeleteModal station |> dispatch)
    ] [
        Icon.icon [] [ Fa.i [ Fa.Solid.Trash ] [] ]
        span [] [ str "Löschen" ]
    ]

let private deleteStationModalConfig dispatch (station: RimPro.StationDto) : ConfirmationModal.Configuration = {
    Headline = "RIMPro Station löschen"
    Text = sprintf "Soll die RIMProStation '%s' gelöscht werden?" station.Name
    OnClose = (fun _ -> dispatch RimProMsg.CloseDeleteModal)
    OnNo = (fun _ -> dispatch RimProMsg.CloseDeleteModal)
    OnYes = (fun _ -> dispatch (RimProMsg.DeleteStation station))
}

let private getSensorName (sensors: RimPro.SelectableSensors) (id: int) : string option =
    sensors |> RimPro.SelectableSensors.tryFind id |> Option.map (fun x -> x.Name)

let private stationToRow dispatch (model: DataModel) (station: RimPro.StationDto) : ReactElement =
    let airSensorName =
        getSensorName model.AvailableSensors.AirSensors station.AirSensorId

    let rainSensorName =
        getSensorName model.AvailableSensors.RainSensors station.RainSensorId

    let firstWetnessSensorName =
        getSensorName model.AvailableSensors.LeafletWetnessSensors station.FirstLeafletWetnessSensorId

    let secondWetnessSensorName =
        station.SecondLeafletWetnessSensorId
        |> Option.bind (getSensorName model.AvailableSensors.LeafletWetnessSensors)

    let names =
        [
            airSensorName
            rainSensorName
            firstWetnessSensorName
            secondWetnessSensorName
        ]
        |> List.choose id
        |> List.distinct
        |> List.sort
        |> String.join ", "

    Columns.columns [ Columns.IsVCentered ] [
        Column.column [] [ str station.Name ]
        Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ str names ]

        Column.column [ Column.Width(Screen.All, Column.IsNarrow) ] [ deleteButton dispatch station ]
    ]

let rowsView dispatch (model: DataModel) =
    match model.Stations with
    | [] -> [
        Heading.p [ Heading.IsSubtitle ] [ str "Es wurden noch keine Stationen erstellt" ]
      ]
    | stations -> List.map (stationToRow dispatch model) stations

let private createIFrame (viewId: string) =
    let src = sprintf "https://rimpro.eu/iframe.xhtml?id=%s" viewId

    iframe [
        Src src
        Class "rimpro-iframe"
        HTMLAttr.Custom("referrer-policy", "origin")
    ] []

let private noRimProView =
    Heading.p [ Heading.IsSubtitle ] [
        str "Die RimPro Modelle wurde noch nicht erstellt."
    ]

let private iFrameView (iframe: RimProIframeDto) : ReactElement =
    let name =
        match iframe.Owner with
        | User -> "Meine Stationen"
        | Group name -> sprintf "Stationen der Gruppe '%s'" name

    Html.details [
        prop.classes [ "accordion" ]

        prop.children [
            Html.summary [ prop.text name ]

            createIFrame iframe.RimProId
        ]
    ]

let private hints: ReactElement list = [
    Html.p [
        prop.className [ "has-text-grey"; "block" ]
        prop.style [ Feliz.style.lineHeight (length.em 1.5) ]
        prop.text "Nach dem Hinzufügen der Station kann es bis zu 2 Arbeitstage dauern bis diese in MYSENS sichtbar ist"
    ]
    Html.p [
        prop.className [ "has-text-grey"; "block" ]
        prop.style [ Feliz.style.lineHeight (length.em 1.5) ]
        prop.children [
            Html.span [
                prop.text "Folgende Modelle werden auf dem Standort aktiviert: "
            ]

            Html.a [
                prop.href "https://rimpro.cloud/de/platform/#row3"
                prop.target "_blank"
                prop.text "Krankheitsmodelle"
            ]
        ]
    ]
]

let content dispatch (model: DataModel) =
    let rows = rowsView dispatch model

    Box.box' [
        GenericOption.CustomClass "grey_box rimpro-box"
    ] [
        Html.section [
            prop.classes [ "rimpro-iframes-section" ]

            prop.children [
                Heading.h1 [] [ str "Modelle" ]

                yield! List.map iFrameView model.Iframes
            ]
        ]

        section [] [
            Heading.h1 [] [ str "Stationen" ]

            yield! rows

            addRowButton dispatch
        ]

        section [] [ yield! hints ]
    ]

let dataView dispatch (model: DataModel) =
    let deleteModal =
        Option.map
            (fun station -> ConfirmationModal.view false (deleteStationModalConfig dispatch station))
            model.DeleteModal

    let creationModal =
        Option.map (fun modalModel -> Forms.RimPro.view (FormMsg >> dispatch) modalModel) model.CreationModal

    [
        Container.container [ Container.isFullWidth ] [ content dispatch model ]

        match deleteModal with
        | Some modal -> modal
        | None -> ()

        match creationModal with
        | Some modal -> modal
        | None -> ()
    ]


let view dispatch (model: Model) =
    Loadable.listView (dataView (RimPro >> dispatch)) model